import { motion, useReducedMotion } from 'framer-motion';
import { Avatar } from '@/components/ui/Avatar';
import type { Avatar as AvatarT } from '@/api/types';
import { cn } from '@/lib/cn';

/**
 * A Popin blob, cut out like a paper sticker (docs/DESIGN.md). It moves on
 * events, then rests: `wave` tilts hello once, `cheer` hops twice, `peek`
 * slides up. `idle` holds still (no loops beside text). Purely decorative
 * unless `label` is given; still under reduced motion.
 */
export type MascotMood = 'idle' | 'wave' | 'cheer' | 'peek';

export function Mascot({
  avatar,
  size = 64,
  mood = 'idle',
  delay = 0,
  label,
  className,
  sticker,
}: {
  avatar: Partial<AvatarT> | null | undefined;
  size?: number;
  mood?: MascotMood;
  /** Seconds before the motion starts (stagger several mascots). */
  delay?: number;
  label?: string;
  className?: string;
  /** Cut-out outline, for blobs stuck on a colour field. */
  sticker?: boolean;
}) {
  const reduced = useReducedMotion();
  const animate = reduced
    ? undefined
    : mood === 'wave'
      ? { rotate: [0, -12, 10, -8, 0], y: [0, -2, 0] }
      : mood === 'cheer'
        ? { y: [0, -14, 0, -8, 0], scale: [1, 1.06, 1, 1.03, 1] }
        : mood === 'peek'
          ? { y: [size * 0.6, 0] }
          : undefined;
  const transition = reduced
    ? undefined
    : mood === 'idle'
      ? undefined
      : mood === 'peek'
        ? { type: 'spring' as const, bounce: 0, duration: 0.5, delay }
        : { duration: mood === 'wave' ? 1.2 : 0.9, repeat: mood === 'cheer' ? 1 : 0, ease: 'easeInOut' as const, delay };
  return (
    <motion.span className={cn('inline-block', className)} animate={animate} transition={transition} style={{ originY: 1 }}>
      <Avatar avatar={avatar} size={size} label={label} sticker={sticker} />
    </motion.span>
  );
}
